import { Buffer } from 'buffer'
import { address, crypto, opcodes, payments, Psbt, script, Transaction } from 'bitcoinjs-lib'
import { parsePsbt } from './market/secure-psbt'
import { schnorr } from '@noble/secp256k1'

const NUMS = Buffer.from('50929b74c1a04954b78b4b6035e97a5e078a5a0f28ec96d547bfee9ace803ac0', 'hex')
export interface RecoveryBackup {
	version: 1 | 2
	intentCommitment?: string
	offerId: string
	fundingTxid: string
	vout: number
	value: number
	destination: string
	buyerKey: string
	cosignerKey: string
	delay: number
}

export function recoveryScripts(b: RecoveryBackup) {
	if (
		![1, 2].includes(b.version) ||
		(b.version === 2 && !/^[a-f0-9]{64}$/i.test(b.intentCommitment || '')) ||
		!/^[a-f0-9]{64}$/i.test(b.fundingTxid) ||
		![b.buyerKey, b.cosignerKey].every((k) => /^[a-f0-9]{64}$/i.test(k)) ||
		!Number.isSafeInteger(b.vout) ||
		b.vout < 0 ||
		b.vout > 0xffffffff ||
		!Number.isSafeInteger(b.value) ||
		b.value < 546 ||
		b.value > 21000000e8 ||
		!Number.isInteger(b.delay) ||
		b.delay < 1 ||
		b.delay > 65535
	)
		throw new Error('Invalid recovery backup.')
	address.toOutputScript(b.destination)
	const buyer = Buffer.from(b.buyerKey, 'hex')
	const sale = script.compile([
		...(b.version === 2 ? [Buffer.from(b.intentCommitment!, 'hex'), opcodes.OP_DROP] : []),
		buyer,
		opcodes.OP_CHECKSIG,
		Buffer.from(b.cosignerKey, 'hex'),
		opcodes.OP_CHECKSIGADD,
		opcodes.OP_2,
		opcodes.OP_NUMEQUAL
	])
	const recovery = script.compile([
		script.number.encode(b.delay),
		opcodes.OP_CHECKSEQUENCEVERIFY,
		opcodes.OP_DROP,
		buyer,
		opcodes.OP_CHECKSIG
	])
	const scriptTree: [{ output: Buffer }, { output: Buffer }] = [{ output: sale }, { output: recovery }]
	const payment = payments.p2tr({
		internalPubkey: NUMS,
		scriptTree,
		redeem: { output: recovery, redeemVersion: 0xc0 }
	})
	const salePayment = payments.p2tr({
		internalPubkey: NUMS,
		scriptTree,
		redeem: { output: sale, redeemVersion: 0xc0 }
	})
	const control = payment.witness?.at(-1)
	const saleControl = salePayment.witness?.at(-1)
	if (!payment.output || !control || !saleControl)
		throw new Error('Could not reconstruct recovery script.')
	return { sale, recovery, output: payment.output, control, saleControl }
}

type TapLeaf = { leafVersion: number; script: Buffer; controlBlock: Buffer }

/** The sale leaf's shape: optional `<intent> OP_DROP`, then the 2-of-2 with a 32-byte key in each slot. */
function saleLeafParts(leaf: TapLeaf | undefined) {
	const rawParts = leaf && script.decompile(leaf.script)
	const bound =
		rawParts?.length === 8 &&
		Buffer.isBuffer(rawParts[0]) &&
		rawParts[0].length === 32 &&
		rawParts[1] === opcodes.OP_DROP
	const parts = bound ? rawParts!.slice(2) : rawParts
	if (
		!parts ||
		parts.length !== 6 ||
		!Buffer.isBuffer(parts[0]) ||
		parts[0].length !== 32 ||
		!Buffer.isBuffer(parts[2]) ||
		parts[2].length !== 32
	)
		return null
	return { bound, intent: bound ? (rawParts![0] as Buffer) : undefined, buyer: parts[0], cosigner: parts[2] }
}

/** Whether a leaf script has the 2-of-2 sale shape (and so is not the CSV recovery leaf). */
export function isSaleLeafScript(leafScript: Uint8Array): boolean {
	return !!saleLeafParts({ leafVersion: 0xc0, script: Buffer.from(leafScript), controlBlock: Buffer.alloc(0) })
}

/** Derive only public recovery material; never store signatures or private keys. */
export function backupFromPsbt(
	hex: string,
	index: number,
	offerId: string,
	destination: string,
	delay: number
): RecoveryBackup {
	const psbt = parsePsbt(hex),
		input = psbt.data.inputs[index],
		prev = psbt.txInputs[index]
	const leaves: TapLeaf[] = (input?.tapLeafScript ?? []).map((leaf) => ({
		leafVersion: leaf.leafVersion,
		script: Buffer.from(leaf.script),
		controlBlock: Buffer.from(leaf.controlBlock)
	}))
	// The sale leaf names the keys; find it wherever the API put it.
	const sale = leaves.map((leaf) => saleLeafParts(leaf)).find(Boolean)
	if (!sale || !prev || !input.witnessUtxo)
		throw new Error('The offer has no supported recovery script.')
	const b: RecoveryBackup = {
		version: sale.bound ? 2 : 1,
		...(sale.bound ? { intentCommitment: sale.intent!.toString('hex') } : {}),
		offerId,
		destination,
		delay,
		fundingTxid: Buffer.from(prev.hash).reverse().toString('hex'),
		vout: prev.index,
		value: input.witnessUtxo.value,
		buyerKey: sale.buyer.toString('hex'),
		cosignerKey: sale.cosigner.toString('hex')
	}
	const expected = recoveryScripts(b)
	if (!expected.output.equals(input.witnessUtxo.script))
		throw new Error('Recovery script does not match the escrow.')
	// The wallet signs every leaf that names its key, so a template the API
	// hands over may carry exactly one leaf: this offer's 2-of-2 sale leaf,
	// with its control block. The CSV recovery leaf is only ever attached to
	// the recovery transaction this client builds itself (buildRecovery); a
	// NONE|ANYONECANPAY signature on it would let anyone sweep the escrow
	// once the delay passes.
	const [leaf] = leaves
	if (
		leaves.length !== 1 ||
		leaf.leafVersion !== 0xc0 ||
		!leaf.script.equals(expected.sale) ||
		!leaf.controlBlock.equals(expected.saleControl)
	)
		throw new Error('The escrow carries a script this offer did not agree to. Nothing was funded.')
	return b
}


export function saveRecoveryBackup(b: RecoveryBackup) {
	localStorage.setItem(`ow:offer-recovery:v1:${b.offerId}`, JSON.stringify(b))
	if (!localStorage.getItem(`ow:offer-recovery:v1:${b.offerId}`))
		throw new Error('Save a recovery backup before funding this offer.')
}

export function buildRecovery(
	b: RecoveryBackup,
	fundingHex: string,
	confirmations: number,
	feeRate: number
) {
	const scripts = recoveryScripts(b),
		funding = Transaction.fromHex(fundingHex)
	const output = funding.outs[b.vout]
	if (
		funding.getId() !== b.fundingTxid ||
		!output ||
		output.value !== b.value ||
		!output.script.equals(scripts.output)
	)
		throw new Error('Funding transaction does not match this backup.')
	if (!Number.isInteger(confirmations) || confirmations < b.delay)
		throw new Error(`Recovery requires ${b.delay} confirmations on the funding transaction.`)
	if (!Number.isFinite(feeRate) || feeRate < 1 || feeRate > 500)
		throw new Error('Choose a fee from 1 to 500 sat/vB.')
	// Compute the exact worst-case vsize with a 65-byte SIGHASH_ALL signature.
	const template = new Transaction()
	template.version = 2
	template.addInput(Buffer.from(b.fundingTxid, 'hex').reverse(), b.vout, b.delay)
	template.addOutput(address.toOutputScript(b.destination), b.value)
	template.setWitness(0, [Buffer.alloc(65), scripts.recovery, scripts.control])
	const fee = Math.ceil(template.virtualSize() * feeRate)
	if (b.value - fee < 546)
		throw new Error('This fee would leave too little BTC to refund. Choose a lower fee.')
	const psbt = new Psbt().setVersion(2)
	psbt.addInput({
		hash: b.fundingTxid,
		index: b.vout,
		sequence: b.delay,
		witnessUtxo: output,
		tapInternalKey: NUMS,
		tapLeafScript: [{ leafVersion: 0xc0, script: scripts.recovery, controlBlock: scripts.control }],
		sighashType: Transaction.SIGHASH_ALL
	})
	psbt.addOutput({ address: b.destination, value: b.value - fee })
	return { psbt: psbt.toHex(), fee, refund: b.value - fee }
}

export async function finalizeRecovery(unsigned: string, signed: string, b: RecoveryBackup) {
	const psbt = parsePsbt(signed),
		expected = parsePsbt(unsigned)
	if (
		!psbt.data.globalMap.unsignedTx.toBuffer().equals(expected.data.globalMap.unsignedTx.toBuffer())
	)
		throw new Error('Wallet changed the recovery transaction.')
	const scripts = recoveryScripts(b)
	const leafHash = crypto.taggedHash(
		'TapLeaf',
		Buffer.concat([Buffer.from([0xc0, scripts.recovery.length]), scripts.recovery])
	)
	const sig = psbt.data.inputs[0].tapScriptSig?.find(
		(s) => s.pubkey.toString('hex') === b.buyerKey && s.leafHash.equals(leafHash)
	)?.signature
	if (!sig || sig.length !== 65 || sig[64] !== 1)
		throw new Error('Wallet did not sign the buyer-only recovery.')
	// Serialize the final witness using the validated backup, not wallet-supplied scripts.
	const tx = Transaction.fromBuffer(expected.data.globalMap.unsignedTx.toBuffer())
	const digest = tx.hashForWitnessV1(
		0,
		[scripts.output],
		[b.value],
		Transaction.SIGHASH_ALL,
		leafHash
	)
	if (!(await schnorr.verify(sig.subarray(0, 64), digest, Buffer.from(b.buyerKey, 'hex'))))
		throw new Error('Invalid recovery signature.')
	tx.setWitness(0, [sig, scripts.recovery, scripts.control])
	return tx
}
